import { useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { getPatSummary } from '../../../../services/analytics/analytics.service'
import Loading from '../../../Loading/Loading'
import DataAsOf from './DataAsOf'

const C_MATHS = '#2b5784'
const C_READING = '#2a9d8f'

const ylNum = (y) => {
  const n = parseInt(String(y).replace(/[^\d]/g, ''), 10)
  return Number.isNaN(n) ? 999 : n
}

// Agrega summary por (year_level, scale) con avg ponderado por alumnos, sobre todos los años.
const buildOption = (rows) => {
  const levels = [...new Set(rows.map((r) => r.year_level).filter(Boolean))].sort(
    (a, b) => ylNum(a) - ylNum(b)
  )

  const acc = {} // acc[level][scale] = { sum, n }
  rows.forEach((r) => {
    const lvl = r.year_level
    const students = Number(r.students || 0)
    const avg = Number(r.avg_scale_score || 0)
    if (!lvl || !students || !avg) return
    acc[lvl] = acc[lvl] || {}
    acc[lvl][r.scale_slug] = acc[lvl][r.scale_slug] || { sum: 0, n: 0 }
    acc[lvl][r.scale_slug].sum += avg * students
    acc[lvl][r.scale_slug].n += students
  })

  const avgFor = (lvl, scale) => {
    const c = acc[lvl] && acc[lvl][scale]
    return c && c.n ? +(c.sum / c.n).toFixed(1) : null
  }

  return {
    backgroundColor: 'transparent',
    grid: { left: 46, right: 16, top: 16, bottom: 40 },
    legend: { bottom: 0, textStyle: { fontSize: 11, color: '#666' }, itemHeight: 10, itemWidth: 14 },
    xAxis: {
      type: 'category',
      data: levels.map((l) => 'Year ' + l),
      axisLabel: { fontSize: 11, color: '#666' },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#e0e0e0' } }
    },
    yAxis: {
      type: 'value',
      scale: true,
      name: 'Avg scale score',
      nameTextStyle: { fontSize: 10, color: '#aaa', align: 'left' },
      axisLabel: { fontSize: 10, color: '#aaa' },
      splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
    },
    series: [
      {
        name: 'PAT Maths',
        type: 'bar',
        barWidth: '32%',
        itemStyle: { color: C_MATHS, borderRadius: [3, 3, 0, 0] },
        label: { show: true, position: 'top', fontSize: 9, color: '#333' },
        data: levels.map((l) => avgFor(l, 'pat-maths-scale'))
      },
      {
        name: 'PAT Reading',
        type: 'bar',
        barWidth: '32%',
        itemStyle: { color: C_READING, borderRadius: [3, 3, 0, 0] },
        label: { show: true, position: 'top', fontSize: 9, color: '#333' },
        data: levels.map((l) => avgFor(l, 'pat-reading-scale'))
      }
    ],
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } }
  }
}

const PatMathsVsReading = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    getPatSummary()
      .then((res) => setData(res?.data || []))
      .catch((err) => setError(err?.message || 'Error loading PAT summary'))
      .finally(() => setLoading(false))
  }, [])

  const option = useMemo(() => buildOption(data), [data])

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: C_MATHS }}>
          Maths vs Reading by year level
        </h2>
        <DataAsOf />
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <div style={{ padding: 24, color: '#c0392b' }}>
          <strong>Error:</strong> {error}
        </div>
      ) : !data.length ? (
        <p style={{ color: '#888', padding: 24 }}>No PAT data yet — run a sync and ingest first.</p>
      ) : (
        <ReactECharts
          option={option}
          style={{ height: 360, width: '100%' }}
          opts={{ renderer: 'canvas' }}
          notMerge
        />
      )}
    </div>
  )
}

export default PatMathsVsReading
